import { useMemo, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Download, ExternalLink, Pencil, RotateCcw, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DataTable,
  DataTableCell,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
} from '@/components/ui/data-table';
import { Pill } from '@/components/ui/pill';
import { Pagination } from '@/components/ui/pagination';
import { cn } from '@/lib/utils';
import type { BulkAction, RowOutcome } from '@/lib/bulk-import/copy';
import type { ImportRow } from '@/lib/bulk-import/rows';
import { HandleCell, ProgressBar, StepPanel, plural } from './shared';

const PAGE_SIZE = 50;

type Filter = 'all' | 'ok' | 'failed' | 'skipped';

interface ResultsStepProps {
  action: BulkAction;
  /** Rows this import acted on, in file order. */
  rows: ImportRow[];
  outcomes: Map<number, RowOutcome>;
  progress: { done: number; total: number } | null;
  /** Logged rows sent without a screenshot. */
  loggedWithoutScreenshot: number;
  onRetryFailed: () => void;
  /** Back to Do it with the failed rows ticked (to add screenshots, say). */
  onFixFailed: () => void;
  onDownload: () => void;
  onStartOver: () => void;
}

function kind(o: RowOutcome | undefined): Exclude<Filter, 'all'> | 'pending' {
  if (!o) return 'pending';
  if (o.skipped) return 'skipped';
  return o.ok ? 'ok' : 'failed';
}

export function ResultsStep(props: ResultsStepProps): JSX.Element {
  const { rows, outcomes, progress } = props;
  const [filter, setFilter] = useState<Filter>('all');
  const [page, setPage] = useState(1);
  const running = progress !== null;

  const totals = useMemo(() => {
    const t = { ok: 0, failed: 0, skipped: 0, warned: 0 };
    for (const r of rows) {
      const o = outcomes.get(r.key);
      const k = kind(o);
      if (k === 'ok') t.ok += 1;
      if (k === 'failed') t.failed += 1;
      if (k === 'skipped') t.skipped += 1;
      if (o?.ok && o.code) t.warned += 1;
    }
    return t;
  }, [rows, outcomes]);

  const shown = filter === 'all' ? rows : rows.filter((r) => kind(outcomes.get(r.key)) === filter);
  const pageRows = shown.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const doneWord = props.action === 'messaged' ? 'marked as messaged' : 'logged';

  return (
    <StepPanel
      title={running ? (props.action === 'messaged' ? 'Marking as messaged…' : 'Logging replies…') : 'Results'}
      description={
        running
          ? 'Keep this tab open until it finishes. Leaving now stops the rest of the run.'
          : `${plural(totals.ok, 'creator')} ${doneWord}${
              totals.failed ? `, ${totals.failed.toLocaleString('en-US')} failed` : ''
            }${totals.skipped ? `, ${totals.skipped.toLocaleString('en-US')} skipped` : ''}.`
      }
      actions={
        !running && (
          <>
            {totals.failed > 0 && (
              <>
                <Button onClick={props.onRetryFailed}>
                  <RotateCcw className="h-3.5 w-3.5" /> Retry {plural(totals.failed, 'failed row')}
                </Button>
                <Button variant="outline" onClick={props.onFixFailed}>
                  <Pencil className="h-3.5 w-3.5" /> Fix failed rows
                </Button>
              </>
            )}
            <Button variant="outline" onClick={props.onDownload}>
              <Download className="h-3.5 w-3.5" /> Download results (CSV)
            </Button>
            <Button variant="outline" onClick={props.onStartOver}>
              <Upload className="h-3.5 w-3.5" /> New import
            </Button>
          </>
        )
      }
    >
      {progress && (
        <ProgressBar done={progress.done} total={progress.total} label={props.action === 'messaged' ? 'Marked' : 'Logged'} />
      )}

      {!running && (
        <div className="grid gap-3 sm:grid-cols-3">
          <Tile label={props.action === 'messaged' ? 'Marked as messaged' : 'Logged'} value={totals.ok} tone="success" />
          <Tile label="Failed" value={totals.failed} tone={totals.failed > 0 ? 'destructive' : 'muted'} />
          <Tile label="Skipped" value={totals.skipped} tone="muted" />
        </div>
      )}

      {!running && props.action === 'log' && (props.loggedWithoutScreenshot > 0 || totals.warned > 0) && (
        <p className="rounded-lg border border-warning/40 bg-warning/[0.06] px-3 py-2 text-sm">
          {plural(Math.max(props.loggedWithoutScreenshot, totals.warned), 'invite')} have no screenshot yet. A screenshot is
          needed to get paid: add each one from{' '}
          <Link to="/portal/invites?tab=replied" className="font-semibold text-brand hover:underline">
            My creators
          </Link>
          .
        </p>
      )}

      {!running && totals.ok > 0 && props.action === 'messaged' && (
        <p className="flex items-start gap-2 text-sm text-muted-foreground">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
          <span>
            They&apos;re on the Messaged tab of{' '}
            <Link to="/portal/invites?tab=messaged" className="font-semibold text-brand hover:underline">
              My creators
            </Link>
            . Log each reply when they answer asking how to join.
          </span>
        </p>
      )}

      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter results">
        {(
          [
            ['all', 'All', rows.length],
            ['ok', 'Done', totals.ok],
            ['failed', 'Failed', totals.failed],
            ['skipped', 'Skipped', totals.skipped],
          ] as Array<[Filter, string, number]>
        )
          .filter(([f, , n]) => f === 'all' || n > 0)
          .map(([f, label, n]) => (
            <button
              key={f}
              type="button"
              aria-pressed={filter === f}
              onClick={() => {
                setFilter(f);
                setPage(1);
              }}
              className={cn(
                'inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition-colors pointer-coarse:h-10',
                filter === f ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-[var(--input-bg)] hover:bg-accent',
              )}
            >
              {label}
              <span className="font-mono tabular-nums opacity-80">{n.toLocaleString('en-US')}</span>
            </button>
          ))}
      </div>

      <DataTable density="outreacher" aria-label="Results">
        <DataTableHeader>
          <tr>
            <DataTableHeaderCell>Creator</DataTableHeaderCell>
            <DataTableHeaderCell>Outcome</DataTableHeaderCell>
            <DataTableHeaderCell>Details</DataTableHeaderCell>
            <DataTableHeaderCell numeric>Row</DataTableHeaderCell>
          </tr>
        </DataTableHeader>
        <tbody>
          {pageRows.map((r) => {
            const o = outcomes.get(r.key);
            const k = kind(o);
            return (
              <DataTableRow key={r.key}>
                <DataTableCell anchor>
                  <HandleCell row={r} />
                </DataTableCell>
                <DataTableCell data-label="Outcome">
                  {k === 'pending' ? (
                    <Pill tone="muted">Waiting</Pill>
                  ) : k === 'skipped' ? (
                    <Pill tone="muted">Skipped</Pill>
                  ) : k === 'ok' ? (
                    <Pill tone={o?.code ? 'pending' : 'success'}>{props.action === 'messaged' ? 'Messaged' : 'Logged'}</Pill>
                  ) : (
                    <Pill tone="failed">Failed</Pill>
                  )}
                </DataTableCell>
                <DataTableCell data-label="Details" className="whitespace-normal">
                  <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                    {o?.message && <span className={cn(k === 'failed' ? 'text-destructive-strong' : 'text-muted-foreground')}>{o.message}</span>}
                    {o?.invitePublicId && (
                      <Link
                        to={`/portal/invites/${o.invitePublicId}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" /> Open invite
                      </Link>
                    )}
                    {!o?.message && !o?.invitePublicId && <span className="text-muted-foreground">—</span>}
                  </span>
                </DataTableCell>
                <DataTableCell numeric muted data-label="Row">
                  {r.rowNumber}
                </DataTableCell>
              </DataTableRow>
            );
          })}
        </tbody>
      </DataTable>
      <Pagination page={page} limit={PAGE_SIZE} total={shown.length} onPageChange={setPage} />
    </StepPanel>
  );
}

function Tile({ label, value, tone }: { label: string; value: number; tone: 'success' | 'destructive' | 'muted' }): JSX.Element {
  return (
    <div className="rounded-lg border border-border px-4 py-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p
        className={cn(
          'mt-1 font-mono text-2xl font-semibold tabular-nums tracking-tight',
          tone === 'success' && 'text-success-strong',
          tone === 'destructive' && 'text-destructive-strong',
        )}
      >
        {value.toLocaleString('en-US')}
      </p>
    </div>
  );
}
